import React, { useEffect, useState } from "react";
import axios from "axios";
import { Tag, Typography, Divider, Checkbox, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { ModalItem, ButtonItem, InputItem, TextAreaItem, ListaItems } from "../../../../../Components/Items";
import { WA } from "../../theme";
import {
  etiquetaTipo,
  PreviewInteractivo,
  limpiarPayload,
  asignarIds,
  payloadVacio,
} from "../Helpers/interactivosHelpers";
import ConstructorInteractivo, { validarConstruido } from "../ConstructorInteractivo";

const { Text } = Typography;

// Modal del chat. Tres vistas:
// - "lista": elegir un interactivo guardado (todos) o crear uno nuevo.
// - "editar": ajustar body/header/footer de uno guardado y enviarlo (botones fijos).
// - "crear": construir uno desde cero y enviarlo; opción de guardarlo como plantilla (con permiso).
const SelectorInteractivos = ({
  visible,
  headersToken,
  empresaId,
  puedeGuardarPlantilla,
  onCancel,
  onEnviar,
}) => {
  const [elementos, setElementos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modo, setModo] = useState("lista");

  // edición de uno guardado
  const [seleccionado, setSeleccionado] = useState(null);
  const [payload, setPayload] = useState(null);

  // creación desde cero
  const [tipoNuevo, setTipoNuevo] = useState("buttons");
  const [payloadNuevo, setPayloadNuevo] = useState(payloadVacio("buttons"));
  const [guardarComo, setGuardarComo] = useState(false);
  const [nombrePlantilla, setNombrePlantilla] = useState("");

  const headers = { headers: headersToken };

  const cargar = () => {
    setLoading(true);
    axios
      .post("whatsapp/elementoListar", { empresaId }, headers)
      .then((r) => {
        if (r.data.success === 2) setElementos(r.data.elementos || []);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!visible) return;
    setModo("lista");
    setSeleccionado(null);
    setPayload(null);
    setTipoNuevo("buttons");
    setPayloadNuevo(payloadVacio("buttons"));
    setGuardarComo(false);
    setNombrePlantilla("");
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const elegir = (el) => {
    setSeleccionado(el);
    setPayload(JSON.parse(JSON.stringify(el.payload || {})));
    setModo("editar");
  };

  const setTextoGuardado = (campo, valor) =>
    setPayload((prev) => ({ ...prev, [campo]: { ...prev[campo], text: valor } }));

  const enviarGuardado = () => {
    if (!payload.body?.text?.trim()) return message.error("El cuerpo no puede quedar vacío.");
    onEnviar({ tipo: "interactivo", tipoInteractivo: seleccionado.tipo, payload: limpiarPayload(payload) });
  };

  const enviarNuevo = async () => {
    const err = validarConstruido(tipoNuevo, payloadNuevo);
    if (err) return message.error(err);
    const limpio = limpiarPayload(asignarIds(tipoNuevo, payloadNuevo));

    if (puedeGuardarPlantilla && guardarComo) {
      if (!nombrePlantilla.trim()) return message.error("Ponle un nombre a la plantilla.");
      try {
        const r = await axios.post(
          "whatsapp/elementoCrear",
          { empresaId, tipo: tipoNuevo, nombre: nombrePlantilla.trim(), payload: limpio },
          headers,
        );
        if (r.data.success === 2) message.success("Plantilla guardada.");
        else message.warning(r.data.message || "Se enviará, pero no se pudo guardar la plantilla.");
      } catch {
        message.warning("Se enviará, pero hubo un error de red al guardar la plantilla.");
      }
    }

    onEnviar({ tipo: "interactivo", tipoInteractivo: tipoNuevo, payload: limpio });
  };

  const titulo =
    modo === "crear" ? "Crear interactivo" : modo === "editar" ? "Enviar interactivo" : "Interactivos";

  return (
    <ModalItem
      title={titulo}
      visible={visible}
      onCancel={onCancel}
      width={560}
      zIndex={1200}
      destroyOnClose
      spinning={false}
    >
      {/* VISTA LISTA */}
      {modo === "lista" && (
        <>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
            <ButtonItem cyan icon={<PlusOutlined />} onClick={() => setModo("crear")}>
              Crear nuevo
            </ButtonItem>
          </div>
          <ListaItems
            dataSource={elementos}
            loading={loading}
            vacioTexto="No hay plantillas guardadas. Crea una con 'Crear nuevo'."
            onItemClick={(el) => elegir(el)}
            titulo={(el) => <Text style={{ color: WA.textPrimary }}>{el.nombre}</Text>}
            descripcion={(el) => (
              <>
                <Tag color={WA.turquesa}>{etiquetaTipo(el.tipo)}</Tag>
                <Text style={{ fontSize: 12, color: WA.textSecondary }}>
                  {el.payload?.body?.text?.slice(0, 60) || ""}
                </Text>
              </>
            )}
          />
        </>
      )}

      {/* VISTA EDITAR (guardado) */}
      {modo === "editar" && seleccionado && (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <Tag color={WA.turquesa}>{etiquetaTipo(seleccionado.tipo)}</Tag>
            <Text strong>{seleccionado.nombre}</Text>
            <ButtonItem gris style={{ marginLeft: "auto" }} onClick={() => setModo("lista")}>
              Volver
            </ButtonItem>
          </div>

          <div style={{ fontWeight: 600, margin: "8px 0 4px" }}>Encabezado (opcional)</div>
          <InputItem
            value={payload.header?.text || ""}
            onChange={(e) => setTextoGuardado("header", e.target.value)}
            maxLength={60}
            placeholder="Sin encabezado"
          />
          <div style={{ fontWeight: 600, margin: "8px 0 4px" }}>Cuerpo *</div>
          <TextAreaItem
            value={payload.body?.text || ""}
            onChange={(e) => setTextoGuardado("body", e.target.value)}
            rows={3}
            maxLength={1024}
          />
          <div style={{ fontWeight: 600, margin: "8px 0 4px" }}>Pie (opcional)</div>
          <InputItem
            value={payload.footer?.text || ""}
            onChange={(e) => setTextoGuardado("footer", e.target.value)}
            maxLength={60}
            placeholder="Sin pie"
          />

          <Divider style={{ margin: "12px 0 8px" }} />
          <Text type="secondary" style={{ fontSize: 12 }}>Vista previa</Text>
          <div style={{ border: `1px solid ${WA.border}`, borderRadius: 8, padding: 10, marginTop: 4, backgroundColor: WA.bg }}>
            <PreviewInteractivo tipo={seleccionado.tipo} payload={payload} />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <ButtonItem rojo onClick={onCancel}>Cancelar</ButtonItem>
            <ButtonItem lima onClick={enviarGuardado}>Enviar</ButtonItem>
          </div>
        </>
      )}

      {modo === "crear" && (
        <>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <ButtonItem gris onClick={() => setModo("lista")}>Volver</ButtonItem>
          </div>

          <ConstructorInteractivo
            tipo={tipoNuevo}
            setTipo={setTipoNuevo}
            payload={payloadNuevo}
            setPayload={setPayloadNuevo}
          />

          <Divider style={{ margin: "12px 0 8px" }} />
          <Text type="secondary" style={{ fontSize: 12 }}>Vista previa</Text>
          <div style={{ border: `1px solid ${WA.border}`, borderRadius: 8, padding: 10, marginTop: 4, backgroundColor: WA.bg }}>
            <PreviewInteractivo tipo={tipoNuevo} payload={payloadNuevo} />
          </div>

          {puedeGuardarPlantilla && (
            <div style={{ marginTop: 14 }}>
              <Checkbox checked={guardarComo} onChange={(e) => setGuardarComo(e.target.checked)}>
                Guardar como plantilla
              </Checkbox>
              {guardarComo && (
                <InputItem
                  placeholder="Nombre de la plantilla"
                  value={nombrePlantilla}
                  onChange={(e) => setNombrePlantilla(e.target.value)}
                  maxLength={60}
                  style={{ marginTop: 8 }}
                />
              )}
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <ButtonItem rojo onClick={onCancel}>Cancelar</ButtonItem>
            <ButtonItem lima onClick={enviarNuevo}>Enviar</ButtonItem>
          </div>
        </>
      )}
    </ModalItem>
  );
};

export default SelectorInteractivos;
